import EventCalendar from '4-sections/EventCalendar/EventCalendar';
import ComponentDelayedInit from 'utils/ComponentDelayedInit';

interface EventLandingBannerProps {
    el: HTMLElement;
}

const EventLandingBanner = ({ el }: EventLandingBannerProps): () => void => {
    const tabs = Array.from(el.querySelectorAll('.event-landing-banner-tab')) as HTMLElement[];
    let activeTab = window.location.hash ? window.location.hash.substring(1) : tabs[0].getAttribute('data-target') || '';

    // Initialize banner variables early so they can be used in handleClick
    let isFixed = false;
    const banner = el.querySelector('.event-landing-banner-tabs') as HTMLElement;
    let bannerOffsetTop = banner.getBoundingClientRect().top + window.scrollY;

    const updateActiveTab = (): void => {
        el.querySelectorAll('.tab-panel-content').forEach(panel => {
            (panel as HTMLElement).style.display = 'none';
        });

        const activePanel = document.querySelector(`#${activeTab}-content`);
        if (activePanel) {
            (activePanel as HTMLElement).style.display = 'block';
        }

        tabs.forEach(tab => {
            const tabTarget = tab.getAttribute('data-target') || '';
            if (tabTarget === activeTab) {
                tab.classList.add('active');
            } else {
                tab.classList.remove('active');
            }
        });

        const contentDivs = document.querySelectorAll('.event-listing-group') as NodeListOf<HTMLElement>;
        contentDivs.forEach(contentDiv => {
            if (contentDiv.id === `${activeTab}-content`) {
                contentDiv.style.display = 'block';
            } else {
                contentDiv.style.display = 'none';
            }
        });

        // Call EventCalendarInit if activeTab is 'calendar'
        if (activeTab === 'calendar') {
            ComponentDelayedInit({ EventCalendar });
        }
    };

    const handleClick = (tab: HTMLElement): void => {
        const tagTarget = tab.getAttribute('data-target') || '';
        if (activeTab !== tagTarget) {
            const previousTab = activeTab;
            activeTab = tagTarget;
            history.pushState(null, '', `#${activeTab}`);

            // Update the active tab first
            updateActiveTab();

            // Scroll the corresponding event-listing-group into view only if user is below it
            const targetContent = document.getElementById(`${activeTab}-content`);
            if (targetContent) {
                const contentTop = targetContent.getBoundingClientRect().top + window.scrollY;
                if (window.scrollY > contentTop - 10) {
                    targetContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }

            // Clean up fixed state when switching away from calendar tab
            if (previousTab === 'calendar' && activeTab !== 'calendar') {
                const hadFixedClass = banner.classList.contains('fixed');

                if (hadFixedClass || isFixed) {
                    banner.classList.remove('fixed');
                    isFixed = false;

                    // Only recalculate if we actually had fixed positioning
                    bannerOffsetTop = banner.getBoundingClientRect().top + window.scrollY;
                }
            }
        }
    };

    tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            handleClick(tab);
        });
    });

    // Initial updates
    el.querySelectorAll('.tab-panel-content').forEach(panel => {
        (panel as HTMLElement).classList.remove('active');
    });
    const initialActivePanel = document.querySelector(`#${activeTab}-content`);
    if (initialActivePanel) {
        (initialActivePanel as HTMLElement).style.display = 'block';
    }
    tabs.forEach(tab => {
        const tabTarget = tab.getAttribute('data-target') || '';
        if (tabTarget === activeTab) {
            tab.classList.add('active');
        } else {
            tab.classList.remove('active');
        }
    });

    const contentDivs = document.querySelectorAll('.event-listing-group') as NodeListOf<HTMLElement>;
    contentDivs.forEach(contentDiv => {
        if (contentDiv.id === `${activeTab}-content`) {
            contentDiv.style.display = 'block';
        } else {
            contentDiv.style.display = 'none';
        }
    });

    // Call EventCalendarInit if initial activeTab is 'calendar'
    if (activeTab === 'calendar') {
        ComponentDelayedInit({ EventCalendar });
    }

    const handleScroll = (): void => {
        const footer = document.querySelector('footer') as HTMLElement;
        const footerRect = footer?.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Check if footer takes up the whole screen
        const footerFillsScreen = footerRect &&
            footerRect.top <= 0 &&
            footerRect.bottom >= viewportHeight;

        if (window.scrollY >= bannerOffsetTop && !isFixed && !footerFillsScreen) {
            banner.classList.add('fixed');
            isFixed = true;
        } else if ((window.scrollY < bannerOffsetTop || footerFillsScreen) && isFixed) {
            banner.classList.remove('fixed');
            isFixed = false;
        }
    };

    const handleResize = (): void => {
        banner.classList.remove('fixed');
        isFixed = false;
        bannerOffsetTop = banner.getBoundingClientRect().top + window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
        tabs.forEach(tab => {
            tab.removeEventListener('click', () => {
                handleClick(tab);
            });
        });
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
    };
};

export default EventLandingBanner;
